/* Small homepage carousel. No dependency on the global Dawn slideshow. */
if (!customElements.get('lecomely-home-hero')) {
  customElements.define('lecomely-home-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-lecomely-home-slide]')];
      this.dots = [...this.querySelectorAll('[data-lecomely-home-dot]')];
      this.pauseButton = this.querySelector('[data-lecomely-home-pause]');
      this.announcement = this.querySelector('[data-lecomely-home-announcement]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.userPaused = this.dataset.autoplay !== 'true';
      this.hovered = false;
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.querySelector('[data-lecomely-home-prev]').addEventListener('click', () => this.go(this.index - 1, true), options);
      this.querySelector('[data-lecomely-home-next]').addEventListener('click', () => this.go(this.index + 1, true), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index, true), options));
      this.pauseButton.addEventListener('click', () => { this.userPaused = !this.userPaused; this.schedule(); }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      // Keyboard focus stops automatic rotation until the user explicitly resumes it.
      this.addEventListener('focusin', (event) => {
        if (event.target === this.pauseButton) return;
        this.userPaused = true;
        this.schedule();
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || /INPUT|SELECT|TEXTAREA/.test(event.target.tagName)) return;
        const next = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.go(next, true);
      }, options);
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || event.target.closest('.lecomely-home-hero__controls')) return;
        this.touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointerup', (event) => {
        if (!this.touchStart) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressClickUntil = Date.now() + 500;
        this.go(this.index + (dx < 0 ? 1 : -1), true);
      }, options);
      this.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
      this.addEventListener('click', (event) => {
        if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.onMotionChange = () => this.schedule();
      this.motion.addEventListener('change', this.onMotionChange);
      this.go(0);
    }
    go(index, manual = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      if (manual) this.userPaused = true;
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== this.index));
        slide.inert = i !== this.index;
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (manual) this.announcement.textContent = this.slides[this.index].getAttribute('aria-label');
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      const reduced = this.motion.matches;
      this.pauseButton.disabled = reduced;
      this.pauseButton.textContent = reduced ? 'Motion reduced' : this.userPaused ? 'Play' : 'Pause';
      this.pauseButton.setAttribute('aria-label', reduced ? 'Automatic slideshow disabled for reduced motion' : this.userPaused ? 'Start slideshow' : 'Pause slideshow');
      if (reduced || this.userPaused || this.hovered || document.hidden || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.motion?.removeEventListener('change', this.onMotionChange);
      this.controller = null;
    }
  });
}

/* Homepage-only enhancement of the existing Dawn ProductForm.
   The parent owns the Ajax request and the native cart owns all cart rendering.
   No prototype, public component, global fetch, or cart implementation is changed. */
customElements.whenDefined('product-form').then(() => {
  if (customElements.get('lecomely-home-product-form')) return;
  const NativeProductForm = customElements.get('product-form');
  customElements.define('lecomely-home-product-form', class extends NativeProductForm {
    constructor() {
      super();
      this.homeStatus = this.querySelector('[data-lecomely-home-status]');
      this.homeCartLink = this.querySelector('[data-lecomely-home-cart-link]');
      this.homePending = false;
    }
    connectedCallback() {
      this.homeObserver = new MutationObserver(() => {
        if (this.homePending && !this.submitButton.classList.contains('loading')) this.finishHomeRequest();
      });
      this.homeObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['class'] });
    }
    onSubmitHandler(event) {
      event.preventDefault();
      if (this.homePending || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.homePending = true;
      this.homeAdded = false;
      this.homeError = '';
      this.error = false;
      this.homeStatus.textContent = 'Adding…';
      this.homeCartLink.hidden = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.prepareNativeHomeCart();
      // A slow/uncertain request is never automatically retried.
      this.homeSlowTimer = setTimeout(() => {
        if (!this.homePending) return;
        this.homeStatus.textContent = 'This is taking longer than expected. Check your cart before trying again.';
        this.homeCartLink.hidden = false;
      }, 15000);
      try {
        super.onSubmitHandler(event);
      } catch (_) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
        this.finishHomeRequest();
      }
    }
    prepareNativeHomeCart() {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      let sections;
      try {
        sections = cart?.getSectionsToRender();
        if (!Array.isArray(sections) || !sections.length || typeof cart.renderContents !== 'function' || typeof cart.setActiveElement !== 'function' || !cart.classList) throw new Error('Native cart unavailable');
        cart.setActiveElement(this.submitButton);
      } catch (_) {
        // Parent ProductForm already redirects successful adds when no cart UI exists.
        this.cart = null;
        return;
      }
      // This adapter belongs only to this homepage form instance.
      this.cart = {
        classList: cart.classList,
        getSectionsToRender: () => sections,
        setActiveElement: (element) => cart.setActiveElement(element),
        renderContents: (response) => {
          this.homeAdded = true;
          this.homeStatus.textContent = 'Added to cart.';
          this.homeCartLink.hidden = false;
          try {
            const complete = sections.every((section) => {
              const html = response.sections?.[section.id];
              if (typeof html !== 'string' || !html.trim()) return false;
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              if (section.id === 'cart-notification-product') return !!parsed.getElementById(`cart-notification-product-${response.key}`) && !!document.getElementById(section.id);
              const content = parsed.querySelector(section.selector || '.shopify-section');
              if (!content) return false;
              if (section.id === 'cart-drawer') return !!content.querySelector('#CartDrawer-Overlay') && !!content.querySelector('.drawer__inner');
              return !!document.getElementById(section.id);
            });
            if (!complete) throw new Error('Native cart sections unavailable');
            cart.renderContents(response);
          } catch (_) {
            // The add succeeded. Use the existing cart page without sending a second add.
            window.location.assign(window.routes.cart_url);
          }
        },
      };
    }
    handleErrorMessage(message = false) {
      super.handleErrorMessage(message);
      if (message && this.homePending) {
        this.homeError = String(message);
        this.homeStatus.textContent = '';
        this.homeCartLink.hidden = false;
      }
    }
    finishHomeRequest() {
      this.homePending = false;
      clearTimeout(this.homeSlowTimer);
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      if (this.homeAdded) {
        this.homeStatus.textContent = 'Added to cart.';
      } else if (!this.homeError) {
        this.homeStatus.textContent = '';
        this.handleErrorMessage('We could not confirm the cart update. Please check View cart before trying again.');
      }
      this.homeCartLink.hidden = false;
    }
    disconnectedCallback() {
      this.homeObserver?.disconnect();
      clearTimeout(this.homeSlowTimer);
    }
  });
});
